import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { ChatService } from './chat.service';
import { AuthService } from './auth.service';

export interface NotificationCount {
  unreadMessages: number;
  totalNotifications: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private apiUrl = `${environment.apiUrl}/api/chat`;
  private notificationCountSubject = new BehaviorSubject<NotificationCount>({ unreadMessages: 0, totalNotifications: 0 });
  private destroy$ = new Subject<void>();
  private refreshInterval?: any;

  constructor(
    private http: HttpClient, 
    private chatService: ChatService,
    private authService: AuthService
  ) {
    // Only start periodic refresh if user is authenticated
    if (this.authService.isAuthenticated()) {
      this.startPeriodicRefresh();
    }
    
    // Listen to authentication state changes
    this.monitorAuthenticationState();
  }

  /**
   * Monitor authentication state and start/stop periodic refresh accordingly
   */
  private monitorAuthenticationState(): void {
    // Subscribe to authentication state changes
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        const isAuthenticated = user !== null;
        
        if (isAuthenticated && !this.refreshInterval) {
          // User just logged in, start periodic refresh
          this.startPeriodicRefresh();
        } else if (!isAuthenticated && this.refreshInterval) {
          // User logged out, stop periodic refresh
          this.stopPeriodicRefresh();
          this.clearNotifications();
        }
      });
  }

  /**
   * Get current notification count
   */
  getNotificationCount(): Observable<NotificationCount> {
    return this.notificationCountSubject.asObservable();
  }

  /**
   * Get current notification count value
   */
  getCurrentNotificationCount(): NotificationCount {
    return this.notificationCountSubject.value;
  }

  /**
   * Fetch notification count from server
   */
  fetchNotificationCount(): Observable<NotificationCount> {
    return this.http.get<any>(`${this.apiUrl}/notification-count`).pipe(
      map(response => {
        if (response.success) {
          return response.data;
        }
        return { unreadMessages: 0, totalNotifications: 0 };
      })
    );
  }

  /**
   * Update notification count
   */
  updateNotificationCount(count: NotificationCount): void {
    this.notificationCountSubject.next(count);
  }

  /**
   * Increment unread message count
   */
  incrementUnreadMessages(): void {
    const current = this.getCurrentNotificationCount();
    this.updateNotificationCount({
      unreadMessages: current.unreadMessages + 1,
      totalNotifications: current.totalNotifications + 1
    });
  }

  /**
   * Decrement unread message count
   */
  decrementUnreadMessages(count: number = 1): void {
    const current = this.getCurrentNotificationCount();
    this.updateNotificationCount({
      unreadMessages: Math.max(0, current.unreadMessages - count),
      totalNotifications: Math.max(0, current.totalNotifications - count)
    });
  }

  /**
   * Clear all notifications
   */
  clearNotifications(): void {
    this.updateNotificationCount({ unreadMessages: 0, totalNotifications: 0 });
  }

  /**
   * Start periodic refresh of notification count
   * Only starts if user is authenticated
   */
  private startPeriodicRefresh(): void {
    // Stop any existing interval first
    this.stopPeriodicRefresh();
    
    // Check authentication before starting
    if (!this.authService.isAuthenticated()) {
      return;
    }
    
    // Refresh every 10 seconds for real-time updates
    this.refreshInterval = setInterval(() => {
      // Check authentication before each call
      if (!this.authService.isAuthenticated()) {
        this.stopPeriodicRefresh();
        this.clearNotifications();
        return;
      }
      
      this.chatService.checkForNewMessages().subscribe({
        next: (response) => {
          // Double-check authentication after response
          if (!this.authService.isAuthenticated()) {
            this.stopPeriodicRefresh();
            this.clearNotifications();
            return;
          }
          
          if (response.success) {
            const newCount = {
              unreadMessages: response.data.unreadCount,
              totalNotifications: response.data.unreadCount
            };
            this.updateNotificationCount(newCount);
          }
        },
        error: (error) => {
          // Stop refresh on authentication errors
          if (error.status === 401 || error.status === 403) {
            this.stopPeriodicRefresh();
            this.clearNotifications();
          }
          console.error('Failed to check for new messages:', error);
        }
      });
    }, 10000);
  }

  /**
   * Stop periodic refresh
   */
  stopPeriodicRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPeriodicRefresh();
  }
}
